import { suggestedInventory } from '../data/inventory';
import type { PricingConfig } from '../data/pricingConfig';
import type { AccessInfo, Bedrooms, FunnelData, QuoteResult } from '../types/funnel';
import { evaluateQuote } from './quoteEngine';

export interface TestResult {
  name: string;
  expected: string;
  actual: string;
  pass: boolean;
}

const acc = (p: Partial<AccessInfo> = {}): AccessInfo => ({
  floor: 'ground', flights: 0, method: 'stairs', elevatorReservation: 'unknown', sharedElevator: 'unknown',
  truckNearEntrance: 'yes', carryBand: '0_20', carryFeet: '', gateAccess: 'no', coiRequired: 'no', notes: '', ...p,
});

function base(bedrooms: Bedrooms, p: Partial<FunnelData> = {}): FunnelData {
  return {
    homeType: 'apartment', bedrooms, originZip: '33602', originStreet: '', originUnit: '', destZip: '33606', destStreet: '', destUnit: '',
    originBuilding: '', destBuilding: '', originCity: 'Tampa', originState: 'FL', originPlaceId: null, originAddrStatus: 'typed', destCity: 'Tampa', destState: 'FL', destPlaceId: null, destAddrStatus: 'typed',
    stops: [], officeSize: null, itemNotes: {}, truckPreference: null,
    services: { reassembly: false, placement: false, garageOnly: false, garageOnlyNote: '', unpacking: false },
    origin: acc(), destination: acc(), inventory: { ...suggestedInventory[bedrooms] }, customItems: [], inventoryUnsure: false,
    needsDisassembly: false, specialItems: [], inventoryNotes: '', packing: 'self', packingProducts: {}, moveDate: '2026-11-02',
    dateUnsure: false, arrival: '8am', arrivalOther: '', firstName: 'Test', lastName: 'Case', phone: '8135550100', email: 't@example.com',
    commLang: 'en', contactChannel: 'call', consentQuote: true, consentSms: false, consentAiCall: false, crewPreference: null,
    acceptedQuoteHash: null, thirdPartyPayer: false, payerName: '', payerEmail: '', payerPhone: '', paymentMethod: 'card', ...p,
  };
}

/** Runs the real quote engine in the browser. These are logic tests, not integration tests. */
export function runEngineTests(cfg: PricingConfig): TestResult[] {
  const out: TestResult[] = [];
  const add = (name: string, expected: string, actual: string, pass: boolean) => out.push({ name, expected, actual, pass });
  const crew = (name: string, d: FunnelData, n: number) => {
    const q = evaluateQuote(d, cfg);
    add(name, `recommend ${n}`, `recommend ${q.crew.recommended}`, q.crew.recommended === n);
  };

  crew('1 BR ground', base('1'), 2);
  crew('1 BR 3rd-floor stairs', base('1', { origin: acc({ floor: 'third', flights: 2 }) }), 2);
  crew('2 BR 2nd floor', base('2', { origin: acc({ floor: 'second', flights: 1 }) }), 2);
  crew('2 BR → 3rd-floor stairs', base('2', { destination: acc({ floor: 'third', flights: 2 }) }), 3);
  crew('3 BR ground', base('3', { homeType: 'house' }), 3);
  crew('4 BR house', base('4', { homeType: 'house' }), 3);

  const pref = evaluateQuote(base('3', { crewPreference: 2 }), cfg);
  add('3 BR customer prefers 2 movers', 'used 2 + review flag', `used ${pref.crew.used}; ${pref.reviewReasons.join(', ') || 'none'}`, pref.crew.used === 2 && pref.reviewReasons.includes('rr_crewPref'));

  const min = evaluateQuote(base('few', { inventory: { box_small: 2 } }), cfg);
  add('Few items → 4 h minimum', `≥ ${cfg.minimumServiceHours} h`, `${min.serviceLow} h`, min.serviceLow >= cfg.minimumServiceHours);

  const ex = evaluateQuote(base('few', { inventory: { box_small: 1 } }), cfg);
  add('Minimum labor = $129 × (4 + 1 travel)', '$645', ex.laborLow !== null ? `$${ex.laborLow}` : 'n/a', ex.laborLow === cfg.rates[2] * (cfg.minimumServiceHours + cfg.travelHours));

  add('Surcharge not calculated without approved base', 'not approved', ex.surchargeBaseApproved ? 'approved' : 'not approved', !ex.surchargeBaseApproved);
  add('Deposit credited once', 'remaining = labor − 99', ex.laborLow !== null && ex.remainingLow !== null ? `${ex.laborLow} − 99 = ${ex.remainingLow}` : 'n/a', ex.remainingLow === (ex.laborLow ?? 0) - cfg.depositAmount);

  const miami = evaluateQuote(base('1', { destZip: '33131' }), cfg);
  add('Tampa → Miami outside area', 'outside', miami.zone, miami.zone === 'outside');

  const high = evaluateQuote(base('3', { origin: acc({ floor: 'fourth_plus', method: 'freight_elevator', carryBand: 'over_100' }) }), cfg);
  add('3 BR high-rise + long carry → review', 'rr_elevator + rr_longCarry', high.reviewReasons.join(', '), high.reviewReasons.includes('rr_elevator') && high.reviewReasons.includes('rr_longCarry'));

  const piano = evaluateQuote(base('2', { specialItems: ['piano'] }), cfg);
  add('Piano → review', 'rr_special', piano.reviewReasons.join(', ') || 'none', piano.reviewReasons.includes('rr_special'));

  const pack = evaluateQuote(base('2', { packing: 'professional', packingProducts: { packed_10: 1 } }), cfg);
  add('Unapproved packing never priced', 'priced by team', pack.packingPriced ? 'priced' : 'priced by team', !pack.packingPriced);

  const a = evaluateQuote(base('1'), cfg);
  const b = evaluateQuote(base('1', { inventory: { ...suggestedInventory['1'], sofa: 2 } }), cfg);
  add('Inventory edit makes accepted estimate stale', 'hash changes', a.hash !== b.hash ? 'changed' : 'same', a.hash !== b.hash);

  return out;
}
